import { gql } from '@apollo/client';
import { DATE_DASH_FORMAT } from '../utils/dateFormats';
import { z, RefinementCtx } from 'zod';
import { getConfig } from '../utils/config';
import { Addon } from './Addons';
import { format, parse } from 'date-fns';
import { ValidationMessages } from './useValidationMessages';

export interface CourseReservation {
  guardian1SoleGuardian: 'Yes' | 'No';
  studentFirstName: string;
  studentLastName: string;
  studentDob: Date | '';
  studentStreetAddress: string;
  studentCity: string;
  studentRegion: string;
  studentPostalCode: string;
  studentCountry: string;
  studentPhone: string;
  studentEmail: string;
  guardian1FirstName: string;
  guardian1LastName: string;
  guardian1StreetAddress: string;
  guardian1City: string;
  guardian1Region: string;
  guardian1PostalCode: string;
  guardian1Country: string;
  guardian1Phone: string;
  guardian1Email: string;
  guardian2FirstName: string;
  guardian2LastName: string;
  guardian2StreetAddress: string;
  guardian2City: string;
  guardian2Region: string;
  guardian2PostalCode: string;
  guardian2Country: string;
  guardian2Phone: string;
  guardian2Email: string;
  studentSchool: string;
  studentAllergies: 'true' | 'false';
  studentMedicalTreatment: 'true' | 'false';
  studentDietaryRequirements: 'true' | 'false';
  studentLearningDifficulties: 'true' | 'false';
  studentAllergiesSpecifics: string;
  studentMedicalTreatmentSpecifics: string;
  studentDietaryRequirementsSpecifics: string;
  studentLearningDifficultiesSpecifics: string;
  courseAddons: { id: string; value: string }[];
}

export const courseReservationIdsQuery = gql`
  query CourseReservationIds($dealId: String!) {
    Records {
      Deals(where: { id: { equals: $dealId } }) {
        _data {
          Deposit_paid {
            value
          }
          Date_of_Contact_Details_Form_Response {
            value
          }
          Last_Addon_Data {
            value
          }
          Contact_Name {
            id {
              value
            }
          }
          Guardian_primary {
            id {
              value
            }
          }
          Guardian_second {
            id {
              value
            }
          }
          Owner {
            id {
              value
            }
          }
          Price {
            value
          }
          Addons_Selection__r {
            _data {
              id {
                value
              }
              Price {
                value
              }
              Sub_Product_Number {
                value
              }
              Type_of_Sub_Product {
                value
              }
              Invoice_Type {
                value
              }
              Addon {
                id {
                  value
                }
              }
            }
          }
        }
      }
    }
  }
`;

export const courseReservationDataQuery = gql`
  query Records($dealId: String!, $studentId: String!, $guardian1Id: String, $guardian1Exists: Boolean!, $guardian2Id: String, $guardian2Exists: Boolean!) {
    Records {
      Deals(where: { id: { equals: $dealId } }) {
        _data {
          Single_parent {
            value
          }
          Current_School {
            value
          }
          Learning_Difficulties {
            value
          }
          Allergies {
            value
          }
          Please_specify_the_Diet {
            value
          }
          Please_specify_the_Disease {
            value
          }
          Please_specify_the_Allergies {
            value
          }
          Please_specify_type_of_Learning_Difficulty {
            value
          }
          Dietary_Requests {
            value
          }
          Chronic_Disease {
            value
          }
        }
      }
      student: Contacts(where: { id: { equals: $studentId } }) {
        _data {
          First_Name {
            value
          }
          Last_Name {
            value
          }
          Date_of_Birth {
            value
          }
          Mailing_Street {
            value
          }
          Mailing_City {
            value
          }
          Mailing_State {
            value
          }
          Mailing_Zip {
            value
          }
          Mailing_Country_Region {
            value
          }
          Mobile {
            value
          }
          Email {
            value
          }
        }
      }
      guardian1: Contacts(where: { id: { equals: $guardian1Id } }) @include(if: $guardian1Exists) {
        _data {
          First_Name {
            value
          }
          Last_Name {
            value
          }
          Mailing_Street {
            value
          }
          Mailing_City {
            value
          }
          Mailing_State {
            value
          }
          Mailing_Zip {
            value
          }
          Mailing_Country_Region {
            value
          }
          Mobile {
            value
          }
          Email {
            value
          }
        }
      }
      guardian2: Contacts(where: { id: { equals: $guardian2Id } }) @include(if: $guardian2Exists) {
        _data {
          First_Name {
            value
          }
          Last_Name {
            value
          }
          Mailing_Street {
            value
          }
          Mailing_City {
            value
          }
          Mailing_State {
            value
          }
          Mailing_Zip {
            value
          }
          Mailing_Country_Region {
            value
          }
          Mobile {
            value
          }
          Email {
            value
          }
        }
      }
    }
  }
`;

export const courseReservationProgramDetailsQuery = gql`
  query Program($productId: String!, $customerCode: String!) {
    Records {
      Products(where: { id: { equals: $productId } }) {
        _data {
          Product_Name {
            value
          }
          Description {
            value
          }
          Pricing_Information__r(where: { Code: { equals: $customerCode } }) {
            _data {
              Pricing_Currency {
                value
              }
              Price {
                value
              }
            }
          }
        }
      }
    }
  }
`;

export const formToApi = (
  formData: CourseReservation,
  dealId: string,
  ownerId: string,
  studentId: string,
  guardian1Id: string,
  guardian2Id: string,
  guardian1SameAddress: boolean,
  guardian2SameAddress: boolean,
  existingAddons: { selectionId: string; addonId: string }[],
  availableAddons: Addon[],
) => {
  const { brandingOrganisation } = getConfig();
  const companyEducatiusGroup = {
    getready: 'Get Ready',
    cci: 'CCI',
    midleton: 'Midleton School',
    jev: 'JEV',
  }[brandingOrganisation];
  // LastName because it is required by CRM, so avoids us checking multiple fields
  const hasGuardian1 = !!formData.guardian1LastName;
  const hasGuardian2 = !!formData.guardian2LastName;
  /* eslint-disable-next-line @typescript-eslint/no-explicit-any */
  const meta: any = {};
  /* eslint-disable-next-line @typescript-eslint/no-explicit-any */
  const contacts: any = [
    {
      id: studentId,
      First_Name: formData.studentFirstName,
      Last_Name: formData.studentLastName,
      Date_of_Birth: formData.studentDob ? format(formData.studentDob, DATE_DASH_FORMAT) : '',
      Mailing_Street: formData.studentStreetAddress,
      Mailing_City: formData.studentCity,
      Mailing_State: formData.studentRegion,
      Mailing_Zip: formData.studentPostalCode,
      Mailing_Country_Region: formData.studentCountry,
      Mobile: formData.studentPhone,
      Email: formData.studentEmail,
      Company_Educatius_Group: companyEducatiusGroup,
    },
  ];
  if (hasGuardian1) {
    meta['Deals.0.Guardian_primary'] = `Contacts.${contacts.length}.id`;
    contacts.push({
      id: guardian1Id || null,
      First_Name: formData.guardian1FirstName,
      Last_Name: formData.guardian1LastName,
      Mailing_Street: guardian1SameAddress ? formData.studentStreetAddress : formData.guardian1StreetAddress,
      Mailing_City: guardian1SameAddress ? formData.studentCity : formData.guardian1City,
      Mailing_State: guardian1SameAddress ? formData.studentRegion : formData.guardian1Region,
      Mailing_Zip: guardian1SameAddress ? formData.studentPostalCode : formData.guardian1PostalCode,
      Mailing_Country_Region: guardian1SameAddress ? formData.studentCountry : formData.guardian1Country,
      Mobile: formData.guardian1Phone,
      Email: formData.guardian1Email,
      Company_Educatius_Group: companyEducatiusGroup,
      Type_of_contact: 'Guardian',
      Owner: ownerId,
    });
  }
  if (hasGuardian2) {
    meta['Deals.0.Guardian_second'] = `Contacts.${contacts.length}.id`;
    contacts.push({
      id: guardian2Id || null,
      First_Name: formData.guardian2FirstName,
      Last_Name: formData.guardian2LastName,
      Mailing_Street: guardian2SameAddress ? formData.studentStreetAddress : formData.guardian2StreetAddress,
      Mailing_City: guardian2SameAddress ? formData.studentCity : formData.guardian2City,
      Mailing_State: guardian2SameAddress ? formData.studentRegion : formData.guardian2Region,
      Mailing_Zip: guardian2SameAddress ? formData.studentPostalCode : formData.guardian2PostalCode,
      Mailing_Country_Region: guardian2SameAddress ? formData.studentCountry : formData.guardian2Country,
      Mobile: formData.guardian2Phone,
      Email: formData.guardian2Email,
      Company_Educatius_Group: companyEducatiusGroup,
      Type_of_contact: 'Guardian',
      Owner: ownerId,
    });
  }
  // setup reverse Map by selectionId which means we can generate the deletion records
  const existingAddonsMap = new Map<string, string>();
  existingAddons.forEach((addon) => {
    existingAddonsMap.set(addon.addonId, addon.selectionId);
  });
  const availableAddonIds = availableAddons.map((a) => a.id);

  const addons = formData.courseAddons
    .map(({ value: addonId }) => {
      if (!availableAddonIds.includes(addonId)) return null;
      const subProduct = availableAddons.find((a) => a.id === addonId);
      /* eslint-disable-next-line @typescript-eslint/no-explicit-any */
      const addon: any = {
        Addon: addonId,
        Price: subProduct!.price,
        Sub_Product_Number: subProduct!.subProductNumber,
        Type_of_Sub_Product: subProduct!.subProductType,
        Invoice_Type: 'Last Payment Date',
      };
      if (existingAddonsMap.has(addonId)) {
        addon.id = existingAddonsMap.get(addonId);
        existingAddonsMap.delete(addonId);
      }
      return addon;
    })
    .filter((a) => a);
  // add sub-forms to delete
  Array.from(existingAddonsMap.entries()).forEach(([, existingAddon]) => {
    addons.push({ _delete: null, id: existingAddon });
  });

  return {
    data: {
      Contacts: contacts,
      Deals: [
        {
          id: dealId,
          Single_parent: formData.guardian1SoleGuardian,
          Current_School: formData.studentSchool,
          Allergies: formData.studentAllergies === 'true',
          Dietary_Requests: formData.studentDietaryRequirements === 'true',
          Chronic_Disease: formData.studentMedicalTreatment === 'true',
          Learning_Difficulties: formData.studentLearningDifficulties === 'true',
          Please_specify_the_Diet: formData.studentDietaryRequirementsSpecifics,
          Please_specify_the_Disease: formData.studentMedicalTreatmentSpecifics,
          Please_specify_the_Allergies: formData.studentAllergiesSpecifics,
          Please_specify_type_of_Learning_Difficulty: formData.studentLearningDifficultiesSpecifics,
          Guardian_primary: guardian1Id || null,
          Guardian_second: guardian2Id || null,
          Addons_Selection: addons,
          Company_Educatius_Group: companyEducatiusGroup,
        },
      ],
    },
    meta,
  };
};

// TODO: graphql typing
/* eslint-disable-next-line @typescript-eslint/no-explicit-any */
export const graphQlToForm = (graphQlResponse: any, addonIds: any[] = []): CourseReservation => {
  const deal = graphQlResponse.Records?.Deals?._data?.[0];
  const student = graphQlResponse.Records?.student?._data?.[0];
  const guardian1 = graphQlResponse.Records?.guardian1?._data?.[0];
  const guardian2 = graphQlResponse.Records?.guardian2?._data?.[0];

  const gqlBoolToForm = (val: boolean | null): 'true' | 'false' => {
    if (val === true) return 'true';
    return 'false';
  };

  // keep track of unique addons so we can eliminate any dupes
  // not using Set as we are not working with simple strings
  const uniqueAddonsMap = new Map<string, string>();

  // track unique addons loaded from CRM
  /* eslint-disable-next-line @typescript-eslint/no-explicit-any */
  addonIds.forEach((addon: any) => {
    if (!uniqueAddonsMap.has(addon.Addon?.id?.value)) {
      uniqueAddonsMap.set(addon.Addon?.id?.value, addon.id?.value);
    }
  });

  const courseAddons = Array.from(uniqueAddonsMap.entries()).map(([addonId]) => ({ id: addonId, value: addonId }));

  return {
    guardian1SoleGuardian: deal?.Single_parent?.value || 'No',
    studentFirstName: student?.First_Name?.value || '',
    studentLastName: student?.Last_Name?.value || '',
    studentDob: student?.Date_of_Birth?.value ? parse(student?.Date_of_Birth?.value, DATE_DASH_FORMAT, new Date()) : '',
    studentStreetAddress: student?.Mailing_Street?.value || '',
    studentCity: student?.Mailing_City?.value || '',
    studentRegion: student?.Mailing_State?.value || '',
    studentPostalCode: student?.Mailing_Zip?.value || '',
    studentCountry: student?.Mailing_Country_Region?.value || '',
    studentPhone: student?.Mobile?.value || '',
    studentEmail: student?.Email?.value || '',
    guardian1FirstName: guardian1?.First_Name?.value || '',
    guardian1LastName: guardian1?.Last_Name?.value || '',
    guardian1StreetAddress: guardian1?.Mailing_Street?.value || '',
    guardian1City: guardian1?.Mailing_City?.value || '',
    guardian1Region: guardian1?.Mailing_State?.value || '',
    guardian1PostalCode: guardian1?.Mailing_Zip?.value || '',
    guardian1Country: guardian1?.Mailing_Country_Region?.value || '',
    guardian1Phone: guardian1?.Mobile?.value || '',
    guardian1Email: guardian1?.Email?.value || '',
    guardian2FirstName: guardian2?.First_Name?.value || '',
    guardian2LastName: guardian2?.Last_Name?.value || '',
    guardian2StreetAddress: guardian2?.Mailing_Street?.value || '',
    guardian2City: guardian2?.Mailing_City?.value || '',
    guardian2Region: guardian2?.Mailing_State?.value || '',
    guardian2PostalCode: guardian2?.Mailing_Zip?.value || '',
    guardian2Country: guardian2?.Mailing_Country_Region?.value || '',
    guardian2Phone: guardian2?.Mobile?.value || '',
    guardian2Email: guardian2?.Email?.value || '',
    studentSchool: deal?.Current_School?.value || '',
    studentAllergies: gqlBoolToForm(deal?.Allergies?.value),
    studentMedicalTreatment: gqlBoolToForm(deal?.Chronic_Disease?.value),
    studentDietaryRequirements: gqlBoolToForm(deal?.Dietary_Requests?.value),
    studentLearningDifficulties: gqlBoolToForm(deal?.Learning_Difficulties?.value),
    studentAllergiesSpecifics: deal?.Please_specify_the_Allergies?.value || '',
    studentMedicalTreatmentSpecifics: deal?.Please_specify_the_Disease?.value || '',
    studentDietaryRequirementsSpecifics: deal?.Please_specify_the_Diet?.value || '',
    studentLearningDifficultiesSpecifics: deal?.Please_specify_type_of_Learning_Difficulty?.value || '',
    courseAddons,
  };
};

export const courseReservationSchema = (messages: ValidationMessages, guardian1SameAddress: boolean, guardian2SameAddress: boolean) => {
  const nonGuardianOptional = z.object({
    guardian1SoleGuardian: z.enum(['Yes', 'No']),
    studentFirstName: z.string().min(0),
    studentLastName: z.string().min(1, messages.studentLastName.required),
    studentDob: z.union([z.date(), z.literal('')]),
    studentStreetAddress: z.string().min(0),
    studentCity: z.string().min(0),
    studentRegion: z.string().min(0),
    studentPostalCode: z.string().min(0),
    studentCountry: z.string().min(0),
    studentPhone: z.string().min(0),
    studentEmail: z.string({ required_error: messages.studentEmail.required }).email({ message: messages.studentEmail.email }),
    studentSchool: z.string().min(0),
    studentAllergies: z.enum(['true', 'false']),
    studentMedicalTreatment: z.enum(['true', 'false']),
    studentDietaryRequirements: z.enum(['true', 'false']),
    studentLearningDifficulties: z.enum(['true', 'false']),
    studentAllergiesSpecifics: z.string().min(0),
    studentMedicalTreatmentSpecifics: z.string().min(0),
    studentDietaryRequirementsSpecifics: z.string().min(0),
    studentLearningDifficultiesSpecifics: z.string().min(0),
    courseAddons: z.array(z.object({ id: z.string(), value: z.string() })),
  });
  const notFilledInOptionalGuardian1 = z.object({
    guardian1FirstName: z.literal(''),
    guardian1LastName: z.literal(''),
    guardian1StreetAddress: z.literal(''),
    guardian1City: z.literal(''),
    guardian1Region: z.literal(''),
    guardian1PostalCode: z.literal(''),
    guardian1Country: z.literal(''),
    guardian1Phone: z.literal(''),
    guardian1Email: z.literal(''),
  });
  const filledInOptionalGuardian1 = z.object({
    guardian1FirstName: z.string().min(0),
    guardian1LastName: z.string().min(1, messages.guardian1LastName.required),
    guardian1StreetAddress: z.string().min(0),
    guardian1City: z.string().min(0),
    guardian1Region: z.string().min(0),
    guardian1PostalCode: z.string().min(0),
    guardian1Country: z.string().min(0),
    guardian1Phone: z.string().min(0),
    guardian1Email: z.string({ required_error: messages.guardian1Email.required }).email({ message: messages.guardian1Email.email }),
  });
  const notFilledInOptionalGuardian2 = z.object({
    guardian2FirstName: z.literal(''),
    guardian2LastName: z.literal(''),
    guardian2StreetAddress: z.literal(''),
    guardian2City: z.literal(''),
    guardian2Region: z.literal(''),
    guardian2PostalCode: z.literal(''),
    guardian2Country: z.literal(''),
    guardian2Phone: z.literal(''),
    guardian2Email: z.literal(''),
  });
  const filledInOptionalGuardian2 = z.object({
    guardian2FirstName: z.string().min(0),
    guardian2LastName: z.string().min(1, messages.guardian2LastName.required),
    guardian2StreetAddress: z.string().min(0),
    guardian2City: z.string().min(0),
    guardian2Region: z.string().min(0),
    guardian2PostalCode: z.string().min(0),
    guardian2Country: z.string().min(0),
    guardian2Phone: z.string().min(0),
    guardian2Email: z.string({ required_error: messages.guardian2Email.required }).email({ message: messages.guardian2Email.email }),
  });
  const optionalGuardians = z.union([
    nonGuardianOptional.merge(notFilledInOptionalGuardian1).merge(notFilledInOptionalGuardian2),
    nonGuardianOptional.merge(filledInOptionalGuardian1).merge(notFilledInOptionalGuardian2),
    nonGuardianOptional.merge(notFilledInOptionalGuardian1).merge(filledInOptionalGuardian2),
    nonGuardianOptional.merge(filledInOptionalGuardian1).merge(filledInOptionalGuardian2),
  ]);
  const requiredFormSchema = z.object({
    studentFirstName: z.string().min(1, messages.studentFirstName.required),
    studentLastName: z.string().min(1, messages.studentLastName.required),
    studentDob: z.date({ required_error: messages.studentDob.required }),
    studentStreetAddress: z.string().min(1, messages.studentStreetAddress.required),
    studentCity: z.string().min(1, messages.studentCity.required),
    studentRegion: z.string().min(1, messages.studentRegion.required),
    studentPostalCode: z.string().min(1, messages.studentPostalCode.required),
    studentCountry: z.string().min(1, messages.studentCountry.required),
    studentPhone: z.string().min(1, messages.studentPhone.required),
    studentEmail: z.string({ required_error: messages.studentEmail.required }).email({ message: messages.studentEmail.email }),
    guardian1FirstName: z.string().min(1, messages.guardian1FirstName.required),
    guardian1LastName: z.string().min(1, messages.guardian1LastName.required),
    guardian1StreetAddress: z.string().refine(
      (val) => guardian1SameAddress || val.length,
      () => ({ message: messages.guardian1StreetAddress.required }),
    ),
    guardian1City: z.string().refine(
      (val) => guardian1SameAddress || val.length,
      () => ({ message: messages.guardian1City.required }),
    ),
    guardian1Region: z.string().refine(
      (val) => guardian1SameAddress || val.length,
      () => ({ message: messages.guardian1Region.required }),
    ),
    guardian1PostalCode: z.string().refine(
      (val) => guardian1SameAddress || val.length,
      () => ({ message: messages.guardian1PostalCode.required }),
    ),
    guardian1Country: z.string().refine(
      (val) => guardian1SameAddress || val.length,
      () => ({ message: messages.guardian1Country.required }),
    ),
    guardian1Phone: z.string().min(1, messages.guardian1Phone.required),
    guardian1Email: z.string({ required_error: messages.guardian1Email.required }).email({ message: messages.guardian1Email.email }),
    studentSchool: z.string().min(0),
    studentAllergies: z.enum(['true', 'false'], { required_error: messages.studentAllergies.required }),
    studentMedicalTreatment: z.enum(['true', 'false'], { required_error: messages.studentMedicalTreatment.required }),
    studentDietaryRequirements: z.enum(['true', 'false'], { required_error: messages.studentDietaryRequirements.required }),
    studentLearningDifficulties: z.enum(['true', 'false'], { required_error: messages.studentLearningDifficulties.required }),
    studentAllergiesSpecifics: z.string(),
    studentMedicalTreatmentSpecifics: z.string(),
    studentDietaryRequirementsSpecifics: z.string(),
    studentLearningDifficultiesSpecifics: z.string(),
    courseAddons: z.array(z.object({ id: z.string(), value: z.string() })),
  });
  const soleGuardianRequired = z.discriminatedUnion('guardian1SoleGuardian', [
    z.object({
      guardian1SoleGuardian: z.literal('Yes'),
      guardian2FirstName: z.string().min(0),
      guardian2LastName: z.string().min(0),
      guardian2StreetAddress: z.string().min(0),
      guardian2City: z.string().min(0),
      guardian2Region: z.string().min(0),
      guardian2PostalCode: z.string().min(0),
      guardian2Country: z.string().min(0),
      guardian2Phone: z.string().min(0),
      guardian2Email: z.string().min(0),
    }),
    z.object({
      guardian1SoleGuardian: z.literal('No'),
      guardian2FirstName: z.string().min(1, messages.guardian2FirstName.required),
      guardian2LastName: z.string().min(1, messages.guardian2LastName.required),
      guardian2StreetAddress: z.string().refine(
        (val) => guardian2SameAddress || val.length,
        () => ({ message: messages.guardian2StreetAddress.required }),
      ),
      guardian2City: z.string().refine(
        (val) => guardian2SameAddress || val.length,
        () => ({ message: messages.guardian2City.required }),
      ),
      guardian2Region: z.string().refine(
        (val) => guardian2SameAddress || val.length,
        () => ({ message: messages.guardian2Region.required }),
      ),
      guardian2PostalCode: z.string().refine(
        (val) => guardian2SameAddress || val.length,
        () => ({ message: messages.guardian2PostalCode.required }),
      ),
      guardian2Country: z.string().refine(
        (val) => guardian2SameAddress || val.length,
        () => ({ message: messages.guardian2Country.required }),
      ),
      guardian2Phone: z.string().min(1, messages.guardian2Phone.required),
      guardian2Email: z.string({ required_error: messages.guardian2Email.required }).email({ message: messages.guardian2Email.email }),
    }),
  ]);
  const allergiesRequired = z.discriminatedUnion('studentAllergies', [
    z.object({
      studentAllergies: z.literal('true'),
      studentAllergiesSpecifics: z.string().min(1, messages.studentAllergiesSpecifics.required),
    }),
    z.object({
      studentAllergies: z.literal('false'),
      studentAllergiesSpecifics: z.string().min(0),
    }),
  ]);
  const medicalRequired = z.discriminatedUnion('studentMedicalTreatment', [
    z.object({
      studentMedicalTreatment: z.literal('true'),
      studentMedicalTreatmentSpecifics: z.string().min(1, messages.studentMedicalTreatmentSpecifics.required),
    }),
    z.object({ studentMedicalTreatment: z.literal('false') }),
  ]);
  const dietRequired = z.discriminatedUnion('studentDietaryRequirements', [
    z.object({
      studentDietaryRequirements: z.literal('true'),
      studentDietaryRequirementsSpecifics: z.string().min(1, messages.studentDietaryRequirementsSpecifics.required),
    }),
    z.object({ studentDietaryRequirements: z.literal('false') }),
  ]);
  const learningRequired = z.discriminatedUnion('studentLearningDifficulties', [
    z.object({
      studentLearningDifficulties: z.literal('true'),
      studentLearningDifficultiesSpecifics: z.string().min(1, messages.studentLearningDifficultiesSpecifics.required),
    }),
    z.object({ studentLearningDifficulties: z.literal('false') }),
  ]);

  const emailsAreUnique = (entries: CourseReservation, ctx: RefinementCtx) => {
    // we check to see how many of the email addresses are filled out (truthy)
    const emailsNonEmpty = [entries.guardian1Email, entries.guardian2Email, entries.studentEmail].filter((e) => e);

    const emails = new Set(emailsNonEmpty);

    if (emails.size === emailsNonEmpty.length) return true;

    if (entries.guardian1Email) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['guardian1Email'],
        fatal: true,
        message: messages.guardian1Email.unique,
      });
    }
    if (entries.guardian2Email) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['guardian2Email'],
        fatal: true,
        message: messages.guardian2Email.unique,
      });
    }
    if (entries.studentEmail) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['studentEmail'],
        fatal: true,
        message: messages.studentEmail.unique,
      });
    }
  };

  return {
    required: requiredFormSchema
      .and(soleGuardianRequired)
      .and(allergiesRequired)
      .and(medicalRequired)
      .and(dietRequired)
      .and(learningRequired)
      .superRefine(emailsAreUnique),
    optional: optionalGuardians.superRefine(emailsAreUnique),
  };
};
